// ヘッダー・透明性バッジ・辞書シート・空状態/オンボーディング・コピートースト。

import { useEffect, useState } from 'react'
import type { Meta } from '../core/loadIndex'

export function Header({ meta }: { meta: Meta | null }) {
  const [sheet, setSheet] = useState(false)
  return (
    <>
      <header className="border-b border-line bg-card">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
          <h1 className="text-xl font-black tracking-wide">
            <span className="inline-block -rotate-2 bg-gradient-to-r from-accent-pink to-accent-vio bg-clip-text italic text-transparent">
              韻鏡
            </span>
            <span className="ml-1.5 text-[10px] font-normal text-mut">いんきょう</span>
          </h1>
          <div className="ml-auto flex items-center gap-1.5 text-[11px]">
            <button
              type="button"
              onClick={() => setSheet(true)}
              className="rounded-full border border-line bg-card px-2 py-0.5 text-mut hover:border-accent-vio/50 hover:text-accent-vio"
            >
              辞書 ▾
            </button>
          </div>
        </div>
      </header>
      {sheet && <DictSheet meta={meta} onClose={() => setSheet(false)} />}
    </>
  )
}

function DictSheet({ meta, onClose }: { meta: Meta | null; onClose: () => void }) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center bg-black/50 sm:items-center" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-t-2xl border border-line bg-card p-5 text-sm text-ink/80 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold text-ink">辞書について</h2>
          <button type="button" onClick={onClose} className="text-mut hover:text-ink">
            閉じる
          </button>
        </div>
        <p className="leading-relaxed">
          収録語の読みは <b className="text-ink">SudachiDict</b>（Apache-2.0）を基に、読みから母音列を
          事前計算しています。固有名詞・数詞は除外した一般語コアです。
        </p>
        <ul className="mt-3 space-y-1 text-mut">
          <li>語数: {meta ? meta.count.toLocaleString() : '—'}</li>
          <li>辞書バージョン: {meta?.version ?? '—'}</li>
          <li>ライセンス: {meta?.license ?? 'Apache-2.0'}</li>
          <li>出典: {meta?.source ?? 'SudachiDict'}</li>
          <li>頻度: Wikipedia日本語版の単語頻度（wikipedia-word-frequency-clean）</li>
        </ul>
        <p className="mt-3 text-xs leading-relaxed text-mut">
          読みは綴りベース、長音化はヒューリスティックです。読み推定には kuromoji（IPADIC, Apache-2.0）を
          使用しています。広告は一切ありません。
        </p>
        <div className="mt-3 flex gap-3 text-xs">
          <a
            href="https://github.com/WorksApplications/SudachiDict"
            target="_blank"
            rel="noreferrer"
            className="text-accent-vio hover:underline"
          >
            SudachiDict
          </a>
          <a
            href="https://unidic.ninjal.ac.jp/"
            target="_blank"
            rel="noreferrer"
            className="text-accent-vio hover:underline"
          >
            UniDic
          </a>
        </div>
      </div>
    </div>
  )
}

const EXAMPLES = ['最高', '革命', 'やってやる', '気持ち', '未来']

export function Onboarding({ onPick }: { onPick: (t: string) => void }) {
  return (
    <div className="mx-auto max-w-md px-4 py-10 text-center">
      <p className="text-2xl font-bold text-ink">母音で、韻を。</p>
      <p className="mt-2 text-sm text-mut">
        漢字・かな・フレーズをそのまま入力。読みは自動で推定し、1タップで直せます。広告は一切なし。
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {EXAMPLES.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => onPick(w)}
            className="rounded-full border border-line bg-card px-3 py-1.5 text-sm text-ink shadow-sm transition hover:border-accent-pink/60 hover:text-accent-pink"
          >
            {w}
          </button>
        ))}
      </div>
    </div>
  )
}

export function EmptyResults({
  onLoosen,
  canLoosen,
  onShallower,
  canShallower,
}: {
  onLoosen: () => void
  canLoosen: boolean
  onShallower: () => void
  canShallower: boolean
}) {
  return (
    <div className="mx-auto max-w-md px-4 py-10 text-center text-mut">
      <p className="text-lg font-semibold text-ink">ぴったりの韻が見つかりません</p>
      <p className="mt-1 text-sm text-mut">条件をゆるめると見つかりやすくなります。</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {canShallower && (
          <button
            type="button"
            onClick={onShallower}
            className="rounded-lg border border-line bg-card px-3 py-1.5 text-sm text-ink hover:border-accent-pink/60 hover:text-accent-pink"
          >
            深さを1つ浅く
          </button>
        )}
        {canLoosen && (
          <button
            type="button"
            onClick={onLoosen}
            className="rounded-lg border border-line bg-card px-3 py-1.5 text-sm text-ink hover:border-accent-pink/60 hover:text-accent-pink"
          >
            特殊モーラをゆるく
          </button>
        )}
        <span className="w-full text-xs text-mut">読みが違うときは上の読みチップを修正</span>
      </div>
    </div>
  )
}

export function CopyToast({ show, raised = false }: { show: boolean; raised?: boolean }) {
  return (
    <div
      data-testid="toast"
      className={`pointer-events-none fixed left-1/2 z-30 -translate-x-1/2 rounded-full bg-accent-vio px-4 py-2 text-sm font-semibold text-white shadow-lg transition ${
        raised ? 'bottom-20' : 'bottom-6'
      } ${show ? 'opacity-100' : 'translate-y-2 opacity-0'}`}
    >
      コピーしました
    </div>
  )
}
